// University of Cincinnati (Uptown campus): public college page (#2652),
// official facts only.
//
// Every figure below was read on 2026-10-01 from the source it names:
// #3859 (2026-10-11), page vs KB: a line now carries the part of UC's stated
// $43,276 budget its page does not itemize, so the total matches the
// school's (and the KB's).
//  - UC's Common Data Set 2025-2026 for the Clifton (Uptown) campus (Office
//    of Institutional Research, "CDS 2025 2026 Clifton Final 06FEB26.pdf"):
//    C1 for Fall 2025 with the residency table; C8 test policy for Fall 2027.
//    Quirk: C1 prints women applicants as "180508", plainly a typo (men
//    15,233 + women 18,508 + unknown 9 = 33,750, the total its residency
//    table gives); the residency table's totals are used. Admits and
//    enrollees agree in both tables. The CDS marks both early decision and
//    early action as not offered and leaves section G blank.
//  - admissions.uc.edu: Deadlines, Admission Decision Process and Timeline,
//    High School FAQs, Test-Optional Admission and its FAQ (the testing source). The deadline
//    table carries no year; it is the page as posted for students applying
//    in fall 2026 for fall 2027 entry. The decision-process page still says
//    test-optional "for the fall 2025 and fall 2026 terms"; the Test-Optional
//    Admission page (no end date) and the CDS C8 for Fall 2027 are used.
//  - uc.edu Student Financial Aid, UC Costs 2026-27: Uptown 2026-27 cohort
//    tuition, and the non-tuition costs for an undergraduate living on campus.
//  - College Scorecard, school id 201885 (net price by family income). The
//    DEMO_KEY API was over its rate limit on 2026-10-01, so the figures were
//    read from the Scorecard's own school page, which shows the same latest
//    year the API serves.
// Nothing here is profile-derived, an odds figure or a student record.
// Re-check a figure against its source before editing it, and move
// `lastChecked` when you do.

export const SOURCES = {
    cds: {
        label: 'University of Cincinnati Common Data Set 2025-2026 (Clifton campus)',
        url: 'https://uc.edu/content/dam/refresh/provost-62/offices/ir/2025-2026ay/CDS%202025%202026%20Clifton%20Final%20%2006FEB26.pdf',
        read: '2026-10-01',
    },
    deadlines: {
        label: 'UC Admissions, Deadlines',
        url: 'https://www.admissions.uc.edu/apply/deadlines.html',
        read: '2026-10-01',
    },
    decision: {
        label: 'UC Admissions, Admission Decision Process and Timeline',
        url: 'https://www.admissions.uc.edu/apply/apply-tips/decision-process.html',
        read: '2026-10-01',
    },
    hsFaq: {
        label: 'UC Admissions, High School Student FAQs',
        url: 'https://www.admissions.uc.edu/information/high-school/hs-faq.html',
        read: '2026-10-01',
    },
    tests: {
        label: 'UC Admissions, Test-Optional FAQ',
        url: 'https://www.admissions.uc.edu/information/high-school/fymc-information/test-optional-admission/faq.html',
        read: '2026-10-01',
    },
    costs: {
        label: 'UC Student Financial Aid, UC Costs 2026-27',
        url: 'https://www.uc.edu/about/financial-aid/starting/costs.html',
        read: '2026-10-01',
    },
    scorecard: {
        label: 'College Scorecard, U.S. Department of Education',
        url: 'https://collegescorecard.ed.gov/school/?201885-University-of-Cincinnati-Main-Campus',
        read: '2026-10-15',
    },
};

const college = {
    slug: 'university-of-cincinnati',
    kbId: 'university_of_cincinnati',
    name: 'University of Cincinnati',
    shortName: 'Cincinnati',
    location: 'Cincinnati, OH',
    control: 'Public',
    lastChecked: '2026-10-11',
    sources: SOURCES,
    // From the KB's sourced branding (UC color palette); decoration only.
    branding: {
        primary: '#E00122',
        secondary: '#000000',
        logo_url: 'https://storage.googleapis.com/college-counselor-assets/university-logos/university_of_cincinnati_logo.png',
    },

    admission: {
        entering: 'Fall 2025',
        cdsCycle: '2025-2026',
        applied: 33750,
        admitted: 29715,
        enrolled: 6497,
        source: 'cds',
        where: 'section C1, residency table totals',
        residency: [
            { group: 'In-state', applied: 19700, admitted: 17820 },
            { group: 'Out-of-state', applied: 11319, admitted: 9621 },
            { group: 'International', applied: 2731, admitted: 2274 },
        ],
        residencySource: 'cds',
        residencyWhere: 'section C1, residency table',
    },
    // Acceptance by school or major: shown only where the school publishes it.
    byMajor: {
        published: false,
        note: 'UC\'s Common Data Set reports one first-year acceptance rate for the Uptown campus, with no rate by college or major. Some majors fill and close to first-year applicants before the March 1 deadline.',
        source: 'cds',
    },
    earlyRounds: {
        title: 'Early action',
        note: 'UC has no early decision plan. Its Common Data Set reports no early action plan and no early counts, though the admissions site describes a nonbinding Early Action deadline of November 1, so there is no early acceptance rate.',
        source: 'cds',
        where: 'sections C21 and C22',
    },

    programs: {
        entering: 'Fall 2027',
        intro: 'These colleges often get more applicants than they have room for and usually close on or soon after November 1, so UC recommends applying by Early Action:',
        rows: [
            { school: 'College-Conservatory of Music (CCM)', requirement: 'Pre-screening materials are encouraged by November 15, and invited applicants have a final audition or interview. Decisions come from the end of February.', source: 'decision' },
            { school: 'College of Nursing', requirement: 'The Bachelor of Science in Nursing has a priority application deadline of November 1.' },
            { school: 'Design, Architecture, Art, and Planning (DAAP); Engineering and Applied Science; Medicine; Lindner College of Business', requirement: 'Applications after November 1 are considered only as space allows.' },
        ],
        source: 'deadlines',
    },

    deadlines: {
        entering: 'Fall 2027',
        headline: { label: 'Application deadline', value: 'March 1 (rolling)' },
        rows: [
            { plan: 'Early Action', deadline: 'November 1', decision: 'Cincinnati Decision Day, the second Wednesday in December', binding: false },
            { plan: 'Rolling admission', deadline: 'March 1', decision: 'On a rolling basis after Cincinnati Decision Day', binding: false },
        ],
        note: 'Apply by Early Action to be considered for the University Honors Program, merit scholarships and the most competitive programs. The confirmation deadline is May 1.',
        source: 'deadlines',
        binding: 'Early Action is nonbinding. UC has no early decision plan and no binding agreement with applicants.',
        bindingSource: 'hsFaq',
    },

    testing: {
        entering: 'Fall 2027',
        policy: 'Test-optional',
        blind: false,
        line: 'UC is test-optional for first-year applicants: every program considers you with or without an ACT or SAT score, and an application without a score is reviewed the same way as one with a score. If you want scores considered, they must come on an official report from ACT or the College Board; scores you report yourself, or scores on your transcript, are not used. Without scores, you are still considered for scholarships if you apply by the November 1 Early Action deadline.',
        source: 'tests',
        cdsLine: 'The Common Data Set also marks the SAT or ACT as not required for fall 2027 applicants, but considered if submitted, with scores due by November 1.',
        cdsWhere: 'section C8',
    },

    // The next cycle (2027-28, fall 2028 entry): only what the school has
    // announced, read on the date below; nothing announced says so (#2651).
    nextCycle: {
        entering: 'Fall 2028',
        checked: '2026-10-01',
        changes: [],
        source: 'deadlines',
    },

    cost: {
        year: '2026-2027',
        columns: ['In-state', 'Out-of-state'],
        rows: [
            { item: 'Tuition (Uptown campus, 2026-27 cohort)', amounts: [14824, 30158] },
            { item: 'Housing and food (on campus)', amounts: [16511, 16511] },
            { item: 'Books and course materials', amounts: [1300, 1300] },
            { item: 'Transportation', amounts: [2038, 3038] },
            { item: 'Miscellaneous', amounts: [3386, 3386] },
            { item: 'Computer (first year)', amounts: [3000, 3000] },
            { item: 'Other costs in UC\'s budget, not itemized on its page', amounts: [2217, 2217] },
        ],
        totals: [43276, 59610],
        note: 'UC budgets $43,276 for a new on-campus Ohio resident; the items it lists add to $41,059, and the last line is the difference. The out-of-state total adds UC\'s nonresident tuition and transportation to that budget.',
        source: 'costs',
        netPrice: {
            average: 24917,
            byIncome: [
                { income: '$0 to $30,000', amount: 16848 },
                { income: '$30,001 to $48,000', amount: 17178 },
                { income: '$48,001 to $75,000', amount: 20592 },
                { income: '$75,001 to $110,000', amount: 26884 },
                { income: 'Over $110,000', amount: 30546 },
            ],
            note: 'What first-year, full-time, in-state students who received federal aid paid after grants and scholarships, by family income, in 2024-25, the latest year the Department of Education publishes. Your own price comes from UC\'s aid offer.',
            tileDetail: 'Per year, after grants, in-state federal aid recipients',
            source: 'scorecard',
        },
    },
};

export default college;
